import { wmsApi } from '../../../config/apiClient';
import { ACCOUNTING_API } from '../../../config/api';

export interface CountTaskDto {
  id: string;
  taskNumber: string;
  taskName: string;
  countType: string;
  zoneId?: string;
  zoneName?: string;
  randomCount?: number;
  assignedCashierId?: number;
  assignedCashierName?: string;
  assignedPersonnelId?: string;
  assignedPersonnelName?: string;
  status: string;
  totalLocations: number;
  countedLocations: number;
  discrepancyCount: number;
  startedAt?: string;
  completedAt?: string;
  approvedBy?: string;
  approvedAt?: string;
  createdAt: string;
}

export interface TaskLocationDto {
  locationId: string;
  locationCode: string;
  locationName: string;
  barcodeData?: string;
  sortOrder: number;
  isCounted: boolean;
}

export interface DiscrepancyItem {
  resultId: string;
  locationId: string;
  locationCode: string;
  productId?: string;
  productName?: string;
  systemQuantity: number;
  countedQuantity: number;
  difference: number;
  isEmpty: boolean;
  status: string;
}

export interface DiscrepancyReport {
  taskId: string;
  taskNumber: string;
  taskName: string;
  countType?: string;
  totalLocations: number;
  matchCount: number;
  discrepancyCount: number;
  locationDiscrepancyCount?: number;
  locationMatchCount?: number;
  discrepancies: DiscrepancyItem[];
  matches: DiscrepancyItem[];
  statisticalAccuracy?: StatisticalAccuracy;
}

export interface StatisticalAccuracy {
  populationTotalLocations: number;
  sampleSize: number;
  locationMatchCount: number;
  locationDiscrepancyCount: number;
  sampleAccuracyPercent: number;
  estimatedMinAccuracyPercent?: number;
  assessment: 'Pass' | 'Warning' | 'Alarm';
  isAlarm: boolean;
  message: string;
  recommendedAction?: string;
}

export interface CountingStats {
  todayTasks: number;
  todayLocations: number;
  pendingApproval: number;
  inProgressTasks: number;
  totalPickableLocations?: number;
  occupiedLocations?: number;
  emptyLocations?: number;
  occupancyRatePercent?: number;
  countablePoolSize?: number;
}

export type CountCriteriaType = 'NegativeStock' | 'LowStock' | 'TopSelling' | 'NoMovement' | 'HighValue' | 'Manual';

export interface CountCriteria {
  Type: CountCriteriaType;
  TopN?: number;
  Days?: number;
  NoMovementDays?: number;
  ProductIds?: string[];
}

export interface CriteriaPreviewProduct {
  productId: string;
  productName?: string;
  barcode?: string;
  stockCode?: string;
  warehouseQuantity: number;
  metric?: number;
  locationCount: number;
}

export interface CriteriaPreview {
  criteriaType: string;
  productCount: number;
  locationCount: number;
  products: CriteriaPreviewProduct[];
}

export interface CountedProduct {
  productId: string;
  productName?: string;
  barcode?: string;
  countedQuantity: number;
  // Sistemdeki kayıtlı miktar (varsa) — sayım ekranında karşılaştırma için.
  systemQuantity?: number;
}

export interface LocationProduct {
  productId: string;
  productName?: string;
  barcode?: string;
  stockCode?: string;
  systemQuantity: number;
}

export interface TodayProductResult {
  resultId: string;
  taskNumber?: string;
  locationCode: string;
  productId?: string;
  productName?: string;
  barcode?: string;
  stockCode?: string;
  systemQuantity: number;
  countedQuantity: number;
  difference: number;
  isEmpty: boolean;
  countedAt?: string;
}

const countingService = {
  getTasks: async (warehouseId: string, params?: { status?: string; page?: number; pageSize?: number }) => {
    const resp = await wmsApi.get(ACCOUNTING_API.WMS_CC_TASKS(warehouseId), { params });
    return resp.data;
  },

  getTaskDetail: async (taskId: string): Promise<CountTaskDto> => {
    const resp = await wmsApi.get(ACCOUNTING_API.WMS_CC_TASK_DETAIL(taskId));
    return resp.data?.data;
  },

  /**
   * Sayım görevi oluşturur. Lokasyon seçimi CountType'a göre API'de yapılır:
   * - Full: depodaki tüm sayılabilir lokasyonlar
   * - Zone: seçilen koridor/bölge ve alt lokasyonları
   * - Random: sayılabilir havuzdan eşit olasılıkla RandomCount kadar lokasyon
   *   (coğrafi yakınlık veya bölge dağılımı hesaba katılmaz)
   * - Single: ZoneId ile verilen tek lokasyon
   */
  createTask: async (data: { WarehouseId: string; TaskName: string; CountType: string; ZoneId?: string; RandomCount?: number; Criteria?: CountCriteria }) => {
    const resp = await wmsApi.post(ACCOUNTING_API.WMS_CC_TASK_CREATE, data);
    return resp.data;
  },

  /**
   * Kriter bazlı (ProductList) sayım için, görev oluşturmadan önce hangi ürünlerin ve kaç
   * lokasyonun sayılacağını önizler.
   */
  previewCriteria: async (warehouseId: string, criteria: CountCriteria): Promise<CriteriaPreview> => {
    const resp = await wmsApi.post(ACCOUNTING_API.WMS_CC_CRITERIA_PREVIEW(warehouseId), criteria);
    return resp.data?.data;
  },

  /**
   * Elle seçim (Manual) kriteri için ürün arar. Yalnızca bu depoda stok lokasyonu olan
   * (sayılabilir) ürünler; ad/barkod/stok koduna göre filtrelenir.
   */
  searchProducts: async (warehouseId: string, term: string): Promise<CriteriaPreviewProduct[]> => {
    const resp = await wmsApi.get(ACCOUNTING_API.WMS_CC_PRODUCT_SEARCH(warehouseId), {
      params: { term: term || undefined, limit: 30 },
    });
    return resp.data?.data ?? [];
  },

  assignTask: async (taskId: string, cashierId: number, cashierName: string): Promise<void> => {
    await wmsApi.post(ACCOUNTING_API.WMS_CC_TASK_ASSIGN, { TaskId: taskId, CashierId: cashierId, CashierName: cashierName });
  },

  cancelTask: async (taskId: string, cancelledBy?: string): Promise<void> => {
    await wmsApi.post(ACCOUNTING_API.WMS_CC_TASK_CANCEL(taskId), null, {
      params: cancelledBy ? { cancelledBy } : undefined,
    });
  },

  requestCancellation: async (
    taskId: string,
    data: { reason: string; personnelId?: string; personnelName?: string; locationCode?: string },
  ): Promise<void> => {
    await wmsApi.post(ACCOUNTING_API.WMS_CC_TASK_REQUEST_CANCEL(taskId), {
      Reason: data.reason,
      PersonnelId: data.personnelId,
      PersonnelName: data.personnelName,
      LocationCode: data.locationCode,
    });
  },

  getMyTasks: async (warehouseId: string, cashierId: number): Promise<CountTaskDto[]> => {
    const resp = await wmsApi.get(ACCOUNTING_API.WMS_CC_MY_TASKS(warehouseId), { params: { cashierId } });
    return resp.data?.data ?? [];
  },

  getMyTasksByPersonnel: async (warehouseId: string, personnelId: string): Promise<CountTaskDto[]> => {
    const resp = await wmsApi.get(ACCOUNTING_API.WMS_CC_MY_TASKS_BY_PERSONNEL(warehouseId), {
      params: { personnelId },
    });
    return resp.data?.data ?? [];
  },

  assignPersonnelToTask: async (taskId: string, personnelId: string, personnelName: string): Promise<void> => {
    await wmsApi.post(ACCOUNTING_API.WMS_CC_TASK_ASSIGN_PERSONNEL, {
      TaskId: taskId,
      PersonnelId: personnelId,
      PersonnelName: personnelName,
    });
  },

  startTask: async (taskId: string): Promise<void> => {
    await wmsApi.post(ACCOUNTING_API.WMS_CC_START(taskId));
  },

  getTaskLocations: async (taskId: string): Promise<TaskLocationDto[]> => {
    const resp = await wmsApi.get(ACCOUNTING_API.WMS_CC_TASK_LOCATIONS(taskId));
    return resp.data?.data ?? [];
  },

  getLocationProducts: async (taskId: string, locationId: string): Promise<LocationProduct[]> => {
    const resp = await wmsApi.get(ACCOUNTING_API.WMS_CC_LOCATION_PRODUCTS(taskId, locationId));
    return resp.data?.data ?? [];
  },

  countLocation: async (taskId: string, locationId: string, products: CountedProduct[], isEmpty: boolean): Promise<{ countedLocations: number }> => {
    const resp = await wmsApi.post(ACCOUNTING_API.WMS_CC_COUNT_LOCATION, {
      TaskId: taskId,
      LocationId: locationId,
      Products: products.map(p => ({ ProductId: p.productId, Barcode: p.barcode, CountedQuantity: p.countedQuantity })),
      IsEmpty: isEmpty,
    });
    return resp.data;
  },

  completeTask: async (taskId: string): Promise<{ discrepancyCount: number }> => {
    const resp = await wmsApi.post(ACCOUNTING_API.WMS_CC_COMPLETE(taskId));
    return resp.data;
  },

  getDiscrepancyReport: async (taskId: string): Promise<DiscrepancyReport> => {
    const resp = await wmsApi.get(ACCOUNTING_API.WMS_CC_DISCREPANCY(taskId));
    return resp.data?.data;
  },

  approveAdjustments: async (taskId: string, approvedBy: string): Promise<void> => {
    await wmsApi.post(ACCOUNTING_API.WMS_CC_APPROVE(taskId), null, { params: { approvedBy } });
  },

  rejectAdjustments: async (taskId: string): Promise<void> => {
    await wmsApi.post(ACCOUNTING_API.WMS_CC_REJECT(taskId));
  },

  getStats: async (warehouseId: string): Promise<CountingStats> => {
    const resp = await wmsApi.get(ACCOUNTING_API.WMS_CC_STATS(warehouseId));
    return resp.data?.data ?? { todayTasks: 0, todayLocations: 0, pendingApproval: 0, inProgressTasks: 0 };
  },

  getTodayProducts: async (warehouseId: string): Promise<TodayProductResult[]> => {
    const resp = await wmsApi.get(ACCOUNTING_API.WMS_CC_TODAY_PRODUCTS(warehouseId));
    return resp.data?.data ?? [];
  },
};

export default countingService;
